import { mcmvFormConfig } from '@nexofacil/schemas';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { GetBrokerResponse } from '@/lib/api';
import { saveFormDraft } from '@/lib/form-persistence';
import type { LandingProps } from '../types';

// =============================================================================
// Conteúdo e regras da página da Angel: fatos do imóvel em destaque, passos,
// as dúvidas que mais reprovam os leads dela e o atalho que começa a análise
// com a renda já marcada.
// =============================================================================

export const CRECI = 'CRECI-SC 72173F';

/** Lançamento sem tabela: verdade sem prometer prioridade. */
export const LAUNCH_NOTE =
  'Valores saem no lançamento. Com a análise pronta, você já sabe a sua faixa e o que falta quando abrir.';

// Recortes do retrato dela sem o texto embutido no avatar original.
const ASSETS = 'https://nexofacil-assets-prod.s3.amazonaws.com/brokers/6a0cf31a08be120a333bb526/avatar';
export const FACE = `${ASSETS}/angel-face-400.jpg`;
export const PORTRAIT = `${ASSETS}/angel-portrait-560.jpg`;

type Property = GetBrokerResponse['properties'][number];

/** Passos reais do funil: formulário de 4 etapas, WhatsApp dela, atendimento. */
export const STEPS: Array<{ title: string; body: string; time?: string }> = [
  {
    title: 'Responda 4 etapas',
    body: 'Renda, FGTS e crédito. Nenhum documento agora.',
    time: '2 min',
  },
  {
    title: 'Fale comigo no WhatsApp',
    body: 'A conversa já começa com as suas respostas. Você não repete nada.',
    time: 'na hora',
  },
  {
    title: 'Eu te mostro o caminho',
    body: 'Sua faixa do MCMV, o subsídio e o que falta pra aprovar.',
  },
  {
    title: 'Visita, proposta e chave',
    body: 'Acompanho você até a assinatura do contrato.',
  },
];

/**
 * As quatro respostas que mais reprovam os leads dela (sem FGTS, renda,
 * entrada, nome). Verdade sem promessa de aprovação.
 */
export const OBJECTIONS: Array<{ worry: string; answer: string }> = [
  {
    worry: 'Não tenho FGTS',
    answer: 'Dá pra seguir. O FGTS ajuda na entrada, mas não é obrigatório.',
  },
  {
    worry: 'Minha renda sozinha não dá',
    answer: 'Junte a renda de quem vai morar com você. A faixa do MCMV conta a renda da família.',
  },
  {
    worry: 'Não tenho dinheiro pra entrada',
    answer:
      'O MCMV tem subsídio de até R$ 55 mil, conforme a faixa, e financiamento em até 420 meses. Na análise eu te mostro quanto falta.',
  },
  {
    worry: 'Meu nome está com restrição',
    answer:
      'Me conta qual é a pendência. Restrição ativa costuma travar o banco, mas tem caminho pra resolver antes.',
  },
];

export type AngelPage = {
  /** Imóvel que lidera a página (o destaque do painel; hoje, o Floratta). */
  lead: Property | null;
  others: Property[];
  /** Começa a análise já vinculada ao imóvel líder. */
  startHref: string | null;
  detailsHref: string | null;
  /** "Pré-lançamento" e afins, tirado das características do imóvel. */
  badge: string | null;
  /** Até 3 fatos curtos do imóvel líder, sem o selo e sem a renda. */
  facts: string[];
  /** O resto das características: vizinhança e comodidades ("perto de tudo"). */
  nearby: string[];
  /** "Renda a partir de R$ 5.500", quando o imóvel informa. */
  income: string | null;
  instagramHandle: string | null;
  firstName: string;
};

export function useAngelPage({ broker, properties }: LandingProps): AngelPage {
  const lead = properties.find((p) => p.featured) ?? properties[0] ?? null;
  const features = lead?.features ?? [];
  const badge =
    features.find((f) => /lançamento/i.test(f)) ??
    (lead?.stage === 'launch' ? 'Lançamento' : lead?.stage === 'ready' ? 'Pronto pra morar' : null);
  const income = features.find((f) => /^renda/i.test(f)) ?? null;
  const rest = features.filter((f) => f !== badge && f !== income);
  const facts = rest.slice(0, 3);
  // Só lugares: comodidade do condomínio ("Lazer completo") não é vizinhança.
  const nearby = rest.slice(3).filter((f) => !/compor renda|lazer/i.test(f));
  const instagram = broker.pageContent.socialLinks?.instagram;
  return {
    lead,
    others: properties.filter((p) => p !== lead),
    startHref: lead ? `/empreendimento/${lead.slug}/qualificar` : null,
    detailsHref: lead ? `/empreendimento/${lead.slug}` : null,
    badge,
    facts,
    nearby,
    income,
    instagramHandle: instagram ? instagram.replace(/\/+$/, '').split('/').pop() ?? null : null,
    firstName: broker.name.split(' ')[0] ?? broker.name,
  };
}

/**
 * true depois que o elemento sai da tela pelo topo. Mostra a barra fixa de
 * ação no celular só quando o botão do topo já não está visível.
 */
export function useScrolledPast<T extends Element>() {
  const ref = useRef<T>(null);
  const [past, setPast] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([entry]) => {
      if (entry) setPast(!entry.isIntersecting && entry.boundingClientRect.top < 0);
    });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return { ref, past };
}

/**
 * Faixas de renda da primeira pergunta de renda do formulário MCMV. Lidas da
 * própria config: a landing nunca diverge do que o formulário pergunta.
 */
const incomeField = mcmvFormConfig.steps
  .flatMap((st) => st.fields)
  .find((f) => f.id === 'monthlyIncomeCents');
// Rótulo curto pra caber em botão de duas colunas no celular; o formulário
// mostra o rótulo completo.
const SHORT: Record<number, string> = {
  400_000: 'Até R$ 5 mil',
  730_000: 'R$ 5 mil a 9,6 mil',
  1_130_000: 'R$ 9,6 mil a 13 mil',
  1_500_000: 'Acima de R$ 13 mil',
};
export const INCOME_OPTIONS: Array<{ value: number; label: string }> = (incomeField?.options ?? []).map((o) => ({
  value: Number(o.value),
  label: SHORT[Number(o.value)] ?? o.label,
}));

/**
 * Começa a análise já com a renda respondida: grava o rascunho do formulário
 * e abre no passo 1 (contato). A renda aparece marcada no passo 2.
 */
export function useStartWithIncome(page: AngelPage, onCta: () => void) {
  const navigate = useNavigate();
  return (cents: number) => {
    saveFormDraft({ step: 1, answers: { monthlyIncomeCents: cents } as never });
    if (page.startHref) navigate(page.startHref);
    else onCta();
  };
}
